import type { W1GrammarAnalysisResponse, W1GrammarQuestion } from './api';
import { escapeWrongHtml as escapeHtml } from './adminWrongQuestions';

export type GrammarPrintVariant = 'student' | 'teacher';
export interface SelectedW1Question {
  question_id: string;
  question_text: string;
  options: string[];
}
const text = (value: string) => escapeHtml(value).replace(/\r?\n/g, '<br />');

export const W1_GRAMMAR_PRINT_CSS = `
  .grammar-section + .grammar-section, .grammar-section + .print-section { break-before: page; page-break-before: always; }
  .grammar-heading { break-after: avoid; page-break-after: avoid; border-bottom: 2px solid #315b78; margin-bottom: 16px; padding-bottom: 10px; }
  .grammar-heading h2 { color: #24465e; font-size: 21px; margin: 0 0 8px; }
  .grammar-description { font-size: 14px; margin: 0; }
  .grammar-structure { border-left: 3px solid #7c98ab; padding: 8px 12px; margin: 0 0 12px; break-inside: avoid; page-break-inside: avoid; font-size: 14px; line-height: 1.7; }
  .grammar-structure p { margin: 2px 0; }
  .grammar-question { margin: 18px 0 24px; break-inside: avoid; page-break-inside: avoid; }
  .grammar-question .prompt { font-size: 16px; font-weight: 600; margin-bottom: 10px; }
  .grammar-answer-line { border-bottom: 1px solid #8795a1; min-height: 30px; margin-left: 24px; }
  .grammar-teacher-answer { color: #24465e; margin: 8px 0 0 24px; font-size: 15px; }
  .grammar-practice-heading { font-size: 16px; margin: 20px 0 12px; break-after: avoid; page-break-after: avoid; }
`;

export function buildW1GrammarPrintSections(
  result: W1GrammarAnalysisResponse,
  selected: SelectedW1Question[],
  variant: GrammarPrintVariant = 'student'
) {
  const selectedById = new Map(selected.map(question => [question.question_id, question]));
  const seen = new Set<string>();
  const categories = result.categories.map(category => ({
    ...category,
    questions: category.questions.filter(question => {
      if (!selectedById.has(question.question_id) || seen.has(question.question_id)) return false;
      seen.add(question.question_id);
      return true;
    })
  })).filter(category => category.questions.length > 0);
  // Preserve selected historical questions even if the current bank no longer returns them.
  const missing: W1GrammarQuestion[] = [...selectedById.values()].filter(question => !seen.has(question.question_id)).map(question => ({
    question_id: question.question_id,
    shuffled: question.question_text,
    shuffled_parts: question.question_text.split(/[;；]/).map(part => part.trim()).filter(Boolean),
    correct_answer: question.options.join('')
  }));
  if (missing.length) categories.push({
    id: 'unclassified-selected', bank_version: result.bank_version, level: result.level,
    name: '未分类', description: '以下历史错题未返回语法分类，保留原题供复习。', structures: [],
    sort_order: 999, is_active: true, is_fallback: true, created_at: '', updated_at: '', questions: missing
  });

  return categories.map(category => `
    <section class="grammar-section" data-grammar-category="${escapeHtml(category.id)}">
      <header class="grammar-heading">
        <h2>${text(category.name)}</h2>
        <p>HSK ${text(result.bank_version)} · Level ${result.level} · W1 · ${variant === 'teacher' ? '教师答案版' : '学生练习版'}</p>
        ${category.description ? `<p class="grammar-description">${text(category.description)}</p>` : ''}
      </header>
      ${category.structures.map(structure => `<div class="grammar-structure">
        ${structure.pattern ? `<p><strong>语法结构：</strong>${text(structure.pattern)}</p>` : ''}
        ${structure.note ? `<p><strong>用法说明：</strong>${text(structure.note)}</p>` : ''}
        ${structure.example ? `<p><strong>例句：</strong>${text(structure.example)}</p>` : ''}
      </div>`).join('')}
      <h3 class="grammar-practice-heading">对应错题练习</h3>
      ${category.questions.map((question, index) => `<article class="grammar-question">
        <div class="prompt">${index + 1}. ${text(question.shuffled_parts.length ? question.shuffled_parts.join('；') : question.shuffled)}</div>
        ${variant === 'teacher'
          ? `<p class="grammar-teacher-answer"><strong>正确答案：</strong>${text(question.correct_answer || '（题库未提供答案）')}</p>`
          : '<div class="grammar-answer-line">答案：</div><div class="grammar-answer-line"></div>'}
      </article>`).join('')}
    </section>`).join('');
}
